import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdExpandMore,
  MdExpandLess,
  MdSearch
} from 'react-icons/md';
import { useGetEventTiers } from '../../hooks';
import { TierDto } from '../../types';
import { currencyFormat } from '../../../common/utils/currency';

interface TierSelectionFieldProps {
  eventId: string;
  selectedTierIds: string[];
  onChange: (tierIds: string[]) => void;
  disabled?: boolean;
}

const TierSelectionField: React.FC<TierSelectionFieldProps> = ({
  eventId,
  selectedTierIds,
  onChange,
  disabled = false
}) => {
  const { t } = useTranslation('events');
  const { data: tierData, isLoading } = useGetEventTiers(eventId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const availableTiers = useMemo(() => {
    let tiers =
      tierData?.items?.filter(
        (tier: TierDto) => tier.type === 'admission' && tier.isVisible !== false
      ) || [];

    if (searchQuery.trim()) {
      tiers = tiers.filter(
        (tier) =>
          tier.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tier.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return tiers.sort((a, b) => {
      const aSelected = selectedTierIds.includes(a.id);
      const bSelected = selectedTierIds.includes(b.id);

      if (aSelected !== bSelected) {
        return bSelected ? 1 : -1;
      }

      return a.name.localeCompare(b.name);
    });
  }, [tierData?.items, searchQuery, selectedTierIds]);

  const handleTierToggle = (tierId: string) => {
    if (disabled) {
      return;
    }

    const isSelected = selectedTierIds.includes(tierId);
    let newSelection: string[];

    if (isSelected) {
      newSelection = selectedTierIds.filter((id) => id !== tierId);
    } else {
      newSelection = [...selectedTierIds, tierId];
    }

    onChange(newSelection);
  };

  const handleSelectAll = () => {
    if (disabled) {
      return;
    }

    if (selectedTierIds.length === availableTiers.length) {
      onChange([]);
    } else {
      onChange(availableTiers.map((tier) => tier.id));
    }
  };

  const isAllSelected =
    selectedTierIds.length === availableTiers.length &&
    availableTiers.length > 0;
  const isSomeSelected =
    selectedTierIds.length > 0 &&
    selectedTierIds.length < availableTiers.length;

  if (isLoading) {
    return (
      <div className="mt-6 space-y-2">
        <div className="block text-sm font-medium text-gray-700">
          {t('settings.discountCodes.page.tierSelectionLabel')}
        </div>
        <div className="text-sm text-gray-500">
          {t('settings.discountCodes.page.loadingTiers')}
        </div>
      </div>
    );
  }

  if (!availableTiers.length && !searchQuery) {
    return (
      <div className="mt-6 space-y-2">
        <div className="block text-sm font-medium text-gray-700">
          {t('settings.discountCodes.page.tierSelectionLabel')}
        </div>
        <div className="text-sm text-gray-500">
          {t('settings.discountCodes.page.noTiersAvailable')}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-2">
      <div className="block text-sm font-medium text-gray-700">
        {t('settings.discountCodes.page.tierSelectionLabel')}
      </div>

      <div className="border rounded-lg bg-white">
        <div
          className="p-3 cursor-pointer border-b flex items-center justify-between hover:bg-gray-50"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium">
              {(() => {
                if (
                  selectedTierIds.length === 0 ||
                  selectedTierIds.length === availableTiers.length
                ) {
                  return t('settings.discountCodes.page.allTiers');
                }
                return t('settings.discountCodes.page.selectedTiers', {
                  count: selectedTierIds.length,
                  total: availableTiers.length
                });
              })()}
            </span>
          </div>
          {isExpanded ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
        </div>

        {/* Expandable tier list */}
        {isExpanded && (
          <div className="p-3 space-y-2">
            <div className="text-xs text-gray-500 mb-3">
              {t('settings.discountCodes.page.tierSelectionHelper')}
            </div>

            {/* Search input */}
            {tierData?.items && tierData.items.length > 5 && (
              <div className="relative mb-3">
                <MdSearch
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder={t('settings.discountCodes.page.searchTiers')}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={disabled}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setSearchQuery('')}
                  >
                    ×
                  </button>
                )}
              </div>
            )}

            <div className="max-h-60 overflow-y-auto space-y-2">
              {/* Select All option */}
              <div
                className={`flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                  disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handleSelectAll}
              >
                {(() => {
                  if (isAllSelected) {
                    return <MdCheckBox size={20} className="text-blue-600" />;
                  }
                  if (isSomeSelected) {
                    return <MdCheckBox size={20} className="text-blue-400" />;
                  }
                  return (
                    <MdCheckBoxOutlineBlank
                      size={20}
                      className="text-gray-400"
                    />
                  );
                })()}
                <span className="text-sm font-medium">
                  {t('settings.discountCodes.page.selectAllTiers')}
                </span>
              </div>

              <hr />

              {/* Individual tiers */}
              {availableTiers.length === 0 && searchQuery ? (
                <div className="text-center text-sm text-gray-500 py-4">
                  {t('settings.discountCodes.page.noTiersFound')}
                </div>
              ) : (
                availableTiers.map((tier: TierDto) => {
                  const isSelected = selectedTierIds.includes(tier.id);
                  return (
                    <div
                      key={tier.id}
                      className={`flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                        disabled ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      onClick={() => handleTierToggle(tier.id)}
                    >
                      {isSelected ? (
                        <MdCheckBox size={20} className="text-blue-600" />
                      ) : (
                        <MdCheckBoxOutlineBlank
                          size={20}
                          className="text-gray-400"
                        />
                      )}
                      <div className="flex-1">
                        <div className="text-sm font-medium">{tier.name}</div>
                        {tier.description && (
                          <div className="text-xs text-gray-500">
                            {tier.description}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {currencyFormat(tier.price)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {selectedTierIds.length === 0 && (
        <div className="text-xs text-gray-500">
          {t('settings.discountCodes.page.noTierRestriction')}
        </div>
      )}
    </div>
  );
};

export default TierSelectionField;
